import React, { useEffect, useRef } from 'react';
import * as d3 from 'd3';

const TimeSeriesChartRenderer = ({ data, config = {} }) => {
  const chartRef = useRef(null);

  useEffect(() => {
    if (!data || !chartRef.current) return;

    // Clear previous content
    d3.select(chartRef.current).selectAll("*").remove();

    const {
      title = 'Time Series Chart',
      series = [],
      width = 800,
      height = 400,
      margin = { top: 40, right: 30, bottom: 60, left: 70 }
    } = data;

    // If no series data, try to extract from cascade progress data
    let chartData = series;
    if (!chartData || chartData.length === 0) {
      // Try to extract time series data from cascade progress
      if (data.cascadeProgressHistory) {
        chartData = data.cascadeProgressHistory.map(progress => ({
          date: new Date(progress.createdOn),
          value: parseFloat(progress.target) || 0,
          planTitle: progress.planTitle,
          assignedTo: progress.assignedTo
        }));
      } else if (data.progressHistory) {
        chartData = data.progressHistory.map(progress => ({
          date: new Date(progress.createdOn),
          value: parseFloat(progress.target) || 0
        }));
      }
    }

    if (!chartData || chartData.length === 0) {
      const container = d3.select(chartRef.current);
      container
        .append('div')
        .style('padding', '40px')
        .style('text-align', 'center')
        .style('color', '#666')
        .style('font-size', '16px')
        .text('No time series data available for visualization.');
      return;
    }

    // Sort data by date
    chartData.sort((a, b) => new Date(a.date) - new Date(b.date));

    const svg = d3.select(chartRef.current)
      .append('svg')
      .attr('width', width)
      .attr('height', height)
      .style('background', '#fafafa')
      .style('border-radius', '8px');

    // Add title
    svg.append('text')
      .attr('x', width / 2)
      .attr('y', 25)
      .attr('text-anchor', 'middle')
      .style('font-size', '18px')
      .style('font-weight', 'bold')
      .style('fill', '#333')
      .text(title);

    // Create scales
    const xScale = d3.scaleTime()
      .domain(d3.extent(chartData, d => new Date(d.date)))
      .range([margin.left, width - margin.right]);

    const yScale = d3.scaleLinear()
      .domain(d3.extent(chartData, d => d.value))
      .nice()
      .range([height - margin.bottom, margin.top]);

    // Create line generator
    const line = d3.line()
      .x(d => xScale(new Date(d.date)))
      .y(d => yScale(d.value))
      .curve(d3.curveMonotoneX);

    // Add grid lines
    svg.append('g')
      .attr('class', 'grid')
      .attr('transform', `translate(0,${height - margin.bottom})`)
      .call(d3.axisBottom(xScale)
        .tickSize(-height + margin.top + margin.bottom)
        .tickFormat('')
      )
      .style('stroke-dasharray', '3,3')
      .style('opacity', 0.3);

    svg.append('g')
      .attr('class', 'grid')
      .attr('transform', `translate(${margin.left},0)`)
      .call(d3.axisLeft(yScale)
        .tickSize(-width + margin.left + margin.right)
        .tickFormat('')
      )
      .style('stroke-dasharray', '3,3')
      .style('opacity', 0.3);

    // Add axes
    svg.append('g')
      .attr('transform', `translate(0,${height - margin.bottom})`)
      .call(d3.axisBottom(xScale)
        .tickFormat(d3.timeFormat('%b %d'))
      )
      .style('font-size', '12px');

    svg.append('g')
      .attr('transform', `translate(${margin.left},0)`)
      .call(d3.axisLeft(yScale))
      .style('font-size', '12px');

    // Add axis labels
    svg.append('text')
      .attr('transform', 'rotate(-90)')
      .attr('y', 20)
      .attr('x', -(height / 2))
      .attr('text-anchor', 'middle')
      .style('font-size', '14px')
      .style('fill', '#666')
      .text('Progress Value');

    svg.append('text')
      .attr('x', width / 2)
      .attr('y', height - 10)
      .attr('text-anchor', 'middle')
      .style('font-size', '14px')
      .style('fill', '#666')
      .text('Date');

    // Add the line
    svg.append('path')
      .datum(chartData)
      .attr('fill', 'none')
      .attr('stroke', '#1565c0')
      .attr('stroke-width', 2)
      .attr('d', line);

    // Add dots
    svg.selectAll('.dot')
      .data(chartData)
      .enter().append('circle')
      .attr('class', 'dot')
      .attr('cx', d => xScale(new Date(d.date)))
      .attr('cy', d => yScale(d.value))
      .attr('r', 4)
      .attr('fill', '#1565c0')
      .style('cursor', 'pointer')
      .on('mouseover', function(event, d) {
        // Tooltip
        const tooltip = d3.select('body').append('div')
          .attr('class', 'tooltip')
          .style('position', 'absolute')
          .style('background', 'rgba(0, 0, 0, 0.8)')
          .style('color', 'white')
          .style('padding', '8px')
          .style('border-radius', '4px')
          .style('font-size', '12px')
          .style('pointer-events', 'none')
          .style('z-index', '1000');

        tooltip.html(`
          <div><strong>Date:</strong> ${new Date(d.date).toLocaleDateString()}</div>
          <div><strong>Value:</strong> ${d.value}</div>
          ${d.planTitle ? `<div><strong>Plan:</strong> ${d.planTitle}</div>` : ''}
          ${d.assignedTo ? `<div><strong>Assigned:</strong> ${d.assignedTo}</div>` : ''}
        `)
        .style('left', (event.pageX + 10) + 'px')
        .style('top', (event.pageY - 10) + 'px');

        d3.select(this).attr('r', 6);
      })
      .on('mouseout', function() {
        d3.selectAll('.tooltip').remove();
        d3.select(this).attr('r', 4);
      });

  }, [data, config]);

  return (
    <div 
      ref={chartRef} 
      style={{ 
        width: '100%', 
        height: 'auto',
        minHeight: '400px',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '20px'
      }} 
    />
  );
};

export default TimeSeriesChartRenderer;